import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import { useBundle, MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import arbManifest from 'translations/arbManifest';
import { Alert, CriticalIcon, Loader, theme } from '@veeqo/ui';
import { t, DEFAULT_LOCALE, resolveBundleUrlWithFallbacks } from './translate';

const MESSAGE_BUNDLE_NAME = 'strings';

interface TranslationContextType {
  t: (stringId: string, args?: Record<string, unknown>) => string | undefined;
}

const TranslationContext = createContext<TranslationContextType | null>(null);

function TranslationContextProvider({ children }: { children: ReactNode }) {
  const [bundle, isBundleLoading] = useBundle(MESSAGE_BUNDLE_NAME);

  const translate = useCallback(
    (stringId: string, args?: Record<string, unknown>) => t(bundle, stringId, args),
    [bundle],
  );

  const value = useMemo(() => ({ t: translate }), [translate]);

  if (isBundleLoading) {
    return <Loader type="TailSpin" />;
  }

  if (!bundle) {
    window.vqMetrics.logErrorDisplayed({ errorCode: 'FAILED_BUNDLE_LOAD' });
    return (
      <Alert
        title="An error occurred."
        iconSlot={
          <CriticalIcon style={{ color: theme.colors.secondary.red.dark }} width="24" height="24" />
        }
      />
    );
  }

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

export function TranslationProvider({ children }: { children: ReactNode }) {
  const documentLocale = document.documentElement.lang || DEFAULT_LOCALE;
  const mbmOptions = useMemo(
    () => ({
      arbManifest,
      defaultLocalizationContext: new LocalizationContextBuilder()
        .withLocale(documentLocale)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
      resolveBundleUrl: (resourcePackPath: string | undefined, locale: string, bundle: string) =>
        resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
    }),
    [documentLocale],
  );

  return (
    <MbmProvider {...mbmOptions}>
      <TranslationContextProvider>{children}</TranslationContextProvider>
    </MbmProvider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return context;
}
